import { Icon } from "@/components/Icons";
import type { FeedSignal } from "../../types";
import type { SignalActionMutations } from "../../hooks";
import { longDate } from "../../format";
import { useRecipientMode } from "../../view-mode";
import { evidenceFor } from "../../mock/detailFixtures";
import { ResearchAssistant } from "./ResearchAssistant";
import { RelationshipsAtAccount } from "./RelationshipsAtAccount";

/**
 * Left column (sealed detail mock): zone 2 intelligence (summary, why it
 * matters, recommended action, quoted talk track, on-demand action row), zone 3
 * evidence + assets (recommended asset, evidence table), then the collapsed
 * Research Assistant strip.
 *
 * The four content fields come only from get_signal_detail; while the modal is
 * painted from the feed-cache row they are empty, so they render as skeleton
 * lines and swap to text when the fetch lands.
 *
 * The sealed mock's "Related contacts" is NOT here — the mock replaced it with
 * the right-rail RKG card. On-demand runs are wired to the existing
 * relationship_check_run / tariff_scan_run ledger actions; the relationship
 * check also renders its grouped-by-owner result below the run buttons.
 */

const VERIF_LABEL: Record<FeedSignal["verification"], string> = {
  verified: "Verified",
  quarantined: "Quarantined",
  unverified: "Unverified",
};

function PanelBody({
  text,
  hydrating,
  lines = 3,
  className,
}: {
  text: string;
  hydrating: boolean;
  lines?: number;
  className?: string;
}): JSX.Element {
  const cls = `sx-body${className ? ` ${className}` : ""}`;
  if (text) return <p className={cls}>{text}</p>;
  if (!hydrating) return <p className={cls} />;
  const widths = ["100%", "92%", "68%", "80%"];
  return (
    <p className={cls} aria-busy="true">
      {Array.from({ length: lines }, (_, i) => (
        <span
          key={i}
          className="sx-skel"
          style={{
            width: widths[i % widths.length],
            marginBottom: i === lines - 1 ? 0 : 8,
          }}
        />
      ))}
    </p>
  );
}

export function ModalMain({
  signal,
  viewerId,
  actions,
  hydrating = false,
}: {
  signal: FeedSignal;
  viewerId: string;
  actions: SignalActionMutations;
  hydrating?: boolean;
}): JSX.Element {
  const recipient = useRecipientMode();
  const evidence = evidenceFor(signal);

  return (
    <>
      {/* ZONE 2 · Intelligence, solid content ground */}
      <div className="sx-z-intel">
        <p className="sx-eyebrow">Summary</p>
        <PanelBody text={signal.content.summary} hydrating={hydrating} />

        <p className="sx-eyebrow">Why it matters</p>
        <PanelBody text={signal.content.why_it_matters} hydrating={hydrating} />

        <p className="sx-eyebrow sx-eyebrow-action">Recommended action</p>
        <PanelBody text={signal.content.recommended_action} hydrating={hydrating} lines={2} />

        <div className="sx-talk">
          <p className="sx-eyebrow">Talk track</p>
          <PanelBody text={signal.content.talk_track} hydrating={hydrating} lines={2} />
        </div>

        {/* Internal-only relationship intelligence. Hidden for a Magic Link
            recipient. The retired Tariff Terrain control is intentionally not
            presented as an action: its ledger event had no result payload. */}
        {recipient ? null : (
          <RelationshipsAtAccount signal={signal} viewerId={viewerId} actions={actions} />
        )}
      </div>

      {/* ZONE 3 · Evidence and assets, recessed ground */}
      <div className="sx-z-evidence">
        <p className="sx-zone-title">Lead magnet</p>
        <div className="sx-lead-magnet" aria-label="Recommended lead signal coming soon">
          <div>
            <p className="sx-lead-magnet-title">Recommended lead signal</p>
            <p className="sx-lead-magnet-copy">
              A relevant conversation starter will be matched to this signal.
            </p>
          </div>
          <span className="sx-lead-magnet-chip">Coming soon</span>
        </div>

        <p className="sx-zone-title sx-sm">Evidence and verification</p>
        <table className="sx-ev-table">
          <caption className="sx-sr-only">Evidence and verification for this signal</caption>
          <tbody>
            <tr>
              <td>Source</td>
              <td>
                {evidence.sourceUrl ? (
                  <a href={evidence.sourceUrl} target="_blank" rel="noreferrer">
                    {evidence.sourceLabel}
                    <Icon name="external-link" />
                  </a>
                ) : (
                  evidence.sourceLabel
                )}
              </td>
            </tr>
            <tr>
              <td>Retrieved</td>
              <td>{longDate(evidence.retrievedAt) ?? "Unknown"}</td>
            </tr>
            <tr>
              <td>Published</td>
              <td>{longDate(evidence.publishedAt) ?? "Unknown"}</td>
            </tr>
            <tr>
              <td>Status</td>
              <td>{VERIF_LABEL[evidence.status]}</td>
            </tr>
            <tr>
              <td>Deadline</td>
              <td>{evidence.deadlineAt ? longDate(evidence.deadlineAt) : "None noted"}</td>
            </tr>
          </tbody>
        </table>
        {/* View source (parity amendment Item 5) — matches the recipient page:
            a glass p2 button below the table opening the source in a new tab.
            The inline table Source link above is retained. */}
        {evidence.sourceUrl ? (
          <div className="sxr-evidence-actions">
            <a
              className="sx-btn sx-btn-p2"
              href={evidence.sourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              View source
              <Icon name="external-link" />
            </a>
          </div>
        ) : null}
      </div>

      <ResearchAssistant signalId={signal.id} company={signal.company} />
    </>
  );
}
